import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Modal from 'react-modal';
import { Helmet } from 'react-helmet-async';
import { Button } from '@mui/material';

import { sortByName, trimObjectName } from 'utils';
import LoadingSpinner from 'components/LoadingSpinner';
import { currentCustomerSelector } from 'store/user/selectors';

import WedgeModal from 'components/WedgeModal';

import ConfirmAction from 'Modals/ConfirmAction';
import UpgradeEcosystem from 'Modals/UpgradeEcosystem';
import RecoverEcosystem from 'Modals/RecoverEcosystem';

import { createErrorMessageSelector, createLoadingSelector } from 'store/utils/selectors';
import { ECOSYSTEM_OPTIONS, ROUTES } from 'enum';
import {
  createEcosystem,
  renameEcosystem,
  removeEcosystem,
  upgradeEcosystem,
  recoverEcosystem,
  fetchEcosystems,
  fetchNSPs,
  removeCurrent,
} from './scenario-actions';
import PendingEcosystemItem from './components/PendingEcosystemItem';
import EcosystemCard from './components/EcosystemCard';
import AddEcosystem from './components/AddEcosystem';

import './ecosystems.scss';

Modal.setAppElement('#modal-root');

const newEcosystemSchema = {
  name: 'New Ecosystem',
  regions: [],
};

const fetchLoadingSelector = createLoadingSelector(['FETCHING_ECOSYSTEMS']);
const createEcosystemLoadingSelector = createLoadingSelector(['CREATE_ECOSYSTEM']);

const upgradeErrorSelector = createErrorMessageSelector(['UPGRADE_ECOSYSTEM']);
const recoverErrorSelector = createErrorMessageSelector(['RECOVER_ECOSYSTEM']);
const removeErrorSelector = createErrorMessageSelector(['REMOVE_ECOSYSTEM']);

function Ecosystems() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { cusID } = useParams();
  const fetchLoading = useSelector(fetchLoadingSelector);
  const createLoading = useSelector(createEcosystemLoadingSelector);
  const ecosystems = useSelector((state) => state.ecosystems.items);
  const removeError = useSelector(removeErrorSelector);
  const upgradeError = useSelector(upgradeErrorSelector);
  const recoverError = useSelector(recoverErrorSelector);
  const user = useSelector((state) => state.user);
  const currentCustomer = useSelector(currentCustomerSelector);
  const [creatingNew, setCreatingNew] = useState(false);
  const [fetchFinished, setFetchFinished] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedItem, setSelectedItem] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      await dispatch(fetchNSPs());
      if (mounted) {
        setFetchFinished(true);
      }
      dispatch(removeCurrent());
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem && ecosystems.length) {
      const ecosystem = ecosystems.find((eco) => eco.uuid === selectedItem.uuid);
      if (selectedItem !== ecosystem) {
        setSelectedItem(ecosystem);
      }
    }
  }, [ecosystems, selectedItem]);

  const closeModal = () => {
    setSelectedOption('');
    setSelectedItem(null);
  };

  const handleRemove = async () => {
    await dispatch(removeEcosystem(selectedItem.uuid));
    if (!removeError) {
      closeModal();
      if (currentCustomer.owner.uuid !== user.uuid && ecosystems.length === 1) {
        history.push({ pathname: ROUTES.CUSTOMERS, state: { from: 'ecosystems' } });
      } else {
        dispatch(fetchEcosystems(cusID));
      }
    }
  };

  const handleUpgrade = async () => {
    await dispatch(upgradeEcosystem(selectedItem.uuid));
    if (!upgradeError) {
      closeModal();
      dispatch(fetchEcosystems(cusID));
    }
  };

  const handleRecover = async () => {
    await dispatch(recoverEcosystem(selectedItem.uuid));
    if (!recoverError) {
      closeModal();
      dispatch(fetchEcosystems(cusID));
    }
  };

  const handleRefresh = async () => {
    setFetchFinished(false);
    await dispatch(fetchEcosystems(cusID));
    setFetchFinished(true);
  };

  const renderEcosystems = () => (
    <>
      <div className="ecosystem-refresh">
        <Button variant="contained" size="large" onClick={handleRefresh}>
          Refresh Ecosystems
        </Button>
      </div>
      <div className="ecosystems">
        {createLoading && <PendingEcosystemItem />}
        {sortByName(ecosystems).map((eco) => (
          <EcosystemCard
            data={eco}
            key={`ecosystems-list-index-${eco.uuid}`}
            onRemove={() => {
              setSelectedOption(ECOSYSTEM_OPTIONS.REMOVE);
              setSelectedItem(eco);
            }}
            onUpgrade={() => {
              setSelectedOption(ECOSYSTEM_OPTIONS.UPGRADE);
              setSelectedItem(eco);
            }}
            onRecover={() => {
              setSelectedOption(ECOSYSTEM_OPTIONS.RECOVER);
              setSelectedItem(eco);
            }}
            onRename={(entity) => dispatch(renameEcosystem(entity))}
          />
        ))}
        {creatingNew && (
          <EcosystemCard
            data={newEcosystemSchema}
            isNew
            onCancel={() => setCreatingNew(false)}
            onSave={(entity) => {
              setCreatingNew(false);
              dispatch(createEcosystem(entity));
            }}
          />
        )}
        {!creatingNew && <AddEcosystem onClick={() => setCreatingNew(true)} />}
        {selectedItem && selectedOption === ECOSYSTEM_OPTIONS.REMOVE && (
          <WedgeModal
            title="Are you sure?"
            onClose={closeModal}
            isOpen={selectedOption === ECOSYSTEM_OPTIONS.REMOVE}
            size="tiny"
          >
            <ConfirmAction
              item={{ type: 'ecosystem', name: selectedItem.name }}
              action="delete"
              onYes={handleRemove}
              onNo={closeModal}
              confirmByTyping
            />
          </WedgeModal>
        )}
        {selectedItem && selectedOption === ECOSYSTEM_OPTIONS.UPGRADE && (
          <WedgeModal
            title={`${trimObjectName(selectedItem.name, 30)} - Upgrade`}
            onClose={closeModal}
            isOpen={selectedOption === ECOSYSTEM_OPTIONS.UPGRADE}
            size="small"
          >
            <UpgradeEcosystem onYes={handleUpgrade} onNo={closeModal} />
          </WedgeModal>
        )}
        {selectedItem && selectedOption === ECOSYSTEM_OPTIONS.RECOVER && (
          <WedgeModal
            title={`${trimObjectName(selectedItem.name, 30)} - Recover`}
            onClose={closeModal}
            isOpen={selectedOption === ECOSYSTEM_OPTIONS.RECOVER}
            size="small"
          >
            <RecoverEcosystem onYes={handleRecover} onNo={closeModal} />
          </WedgeModal>
        )}
      </div>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Ecosystems - {currentCustomer.name} - Sectrify</title>
      </Helmet>
      {fetchLoading || !fetchFinished ? <LoadingSpinner /> : renderEcosystems()}
    </>
  );
}

export default Ecosystems;
